import { db } from '../config/firebase';
import { 
  collection, 
  doc, 
  getDoc,
  onSnapshot, 
  query, 
  where, 
  orderBy,
  limit,
  getDocs
} from 'firebase/firestore';

class AutomowerFirestoreService {
  constructor() {
    this.listeners = new Map();
    this.cacheListeners = new Map();
  }

  /**
   * Retourne l'accountId fixe du robot Automower
   */
  getAccountId() {
    return 'd3888a77-028b-41d9-8721-9da2c1daf767';
  }

  /**
   * Écoute la position actuelle d'un robot en temps réel
   */
  subscribeToPosition(accountId, callback) {
    if (!accountId) {
      console.error('AccountId requis pour subscribeToCurrentPosition');
      return () => {};
    }

    const listenerKey = `current_${accountId}`;
    
    // Nettoyer l'ancien listener s'il existe
    if (this.cacheListeners.has(listenerKey)) {
      this.cacheListeners.get(listenerKey)();
      this.cacheListeners.delete(listenerKey);
    }

    // Utiliser la structure correcte : automower_tracking/{accountId}
    const currentRef = doc(db, 'automower_tracking', accountId);
    
    const unsubscribe = onSnapshot(currentRef, (docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        
        // Calculer si les données sont récentes
        const now = Date.now();
        const lastUpdate = data.updatedAt?.toMillis() || 0;
        const ageInMinutes = (now - lastUpdate) / (1000 * 60);
        
        callback({
          success: true,
          data: {
            ...data,
            timestamp: data.timestamp?.toDate(),
            updatedAt: data.updatedAt?.toDate()
          },
          isStale: ageInMinutes > 2, // Considérer comme périmé après 2 minutes
          ageInMinutes: Math.floor(ageInMinutes)
        });
      } else {
        callback({
          success: false,
          data: null,
          isStale: true,
          ageInMinutes: null
        });
      }
    }, (error) => {
      console.error('Erreur subscription position actuelle:', error);
      callback({
        success: false,
        error: error.message,
        data: null
      });
    });

    this.cacheListeners.set(listenerKey, unsubscribe);
    return unsubscribe;
  }

  /**
   * Alias pour subscribeToPosition (compatibilité)
   */
  subscribeToCurrentPosition(accountId, callback) {
    return this.subscribeToPosition(accountId, callback);
  }

  /**
   * Écoute l'historique des points d'une journée en temps réel
   */
  subscribeToTodayHistory(accountId, date = null, callback) {
    if (!accountId) {
      console.error('AccountId requis pour subscribeToTodayHistory');
      return () => {};
    }

    const targetDate = date || new Date().toISOString().split('T')[0]; // YYYY-MM-DD
    const listenerKey = `history_${accountId}_${targetDate}`;
    
    // Nettoyer l'ancien listener s'il existe
    if (this.cacheListeners.has(listenerKey)) {
      this.cacheListeners.get(listenerKey)();
      this.cacheListeners.delete(listenerKey);
    }

    // Structure: automower_tracking/{accountId}/history/{date}/points (ou directement les documents)
    const historyRef = collection(db, 'automower_tracking', accountId, 'history', targetDate, 'points');
    const q = query(historyRef, orderBy('timestamp', 'desc'), limit(2000));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const points = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate(),
        recordedAt: doc.data().recordedAt?.toDate()
      }));

      callback({
        success: true,
        points,
        date: targetDate,
        count: points.length
      });
    }, (error) => {
      console.error('Erreur subscription historique:', error);
      callback({
        success: false,
        error: error.message,
        points: [],
        count: 0
      });
    });

    this.cacheListeners.set(listenerKey, unsubscribe);
    return unsubscribe;
  }

  /**
   * Écoute les zones de travail en temps réel
   */
  subscribeToZones(accountId, callback) {
    if (!accountId) {
      console.error('AccountId requis pour subscribeToZones');
      return () => {};
    }

    const listenerKey = `zones_${accountId}`;
    
    // Nettoyer l'ancien listener s'il existe
    if (this.cacheListeners.has(listenerKey)) {
      this.cacheListeners.get(listenerKey)();
      this.cacheListeners.delete(listenerKey);
    }

    const zonesRef = doc(db, 'automower_zones', accountId);
    
    const unsubscribe = onSnapshot(zonesRef, (docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        const zones = data.zones || [];

        callback({
          success: true,
          zones,
          count: zones.length
        });
      } else {
        callback({
          success: true,
          zones: [],
          count: 0
        });
      }
    }, (error) => {
      console.error('Erreur subscription zones:', error);
      callback({
        success: false,
        error: error.message,
        zones: [],
        count: 0
      });
    });

    this.cacheListeners.set(listenerKey, unsubscribe);
    return unsubscribe;
  }

  /**
   * Écoute les statistiques du jour en temps réel
   */
  subscribeToDailyStats(accountId, date = null, callback) {
    if (!accountId) {
      console.error('AccountId requis pour subscribeToDailyStats');
      return () => {};
    }

    const targetDate = date || new Date().toISOString().split('T')[0]; // YYYY-MM-DD
    const listenerKey = `stats_${accountId}_${targetDate}`;
    
    // Nettoyer l'ancien listener s'il existe
    if (this.cacheListeners.has(listenerKey)) {
      this.cacheListeners.get(listenerKey)();
      this.cacheListeners.delete(listenerKey);
    }

    // Structure correcte: automower_stats/{accountId}/daily/{date}
    const statsRef = doc(db, 'automower_stats', accountId, 'daily', targetDate);
    
    const unsubscribe = onSnapshot(statsRef, (docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        
        callback({
          success: true,
          stats: {
            ...data,
            firstSeen: data.firstSeen?.toDate(),
            lastSeen: data.lastSeen?.toDate(),
            updatedAt: data.updatedAt?.toDate(),
            // Formatter les heures de début/fin par zone
            zoneActivity: data.zoneActivity || {},
            // Formatter les sessions de tonte/charge avec horaires
            sessions: data.sessions || []
          },
          date: targetDate
        });
      } else {
        // Aucune stat pour cette date
        callback({
          success: true,
          stats: {
            date: targetDate,
            totalPoints: 0,
            mowingTime: 0,
            chargingTime: 0,
            parkedTime: 0,
            zonesVisited: [],
            batteryLevels: [],
            activities: {},
            zoneActivity: {},
            sessions: []
          },
          date: targetDate
        });
      }
    }, (error) => {
      console.error('Erreur subscription stats:', error);
      callback({
        success: false,
        error: error.message,
        stats: null
      });
    });

    this.cacheListeners.set(listenerKey, unsubscribe);
    return unsubscribe;
  }

  /**
   * Écoute l'historique de plusieurs jours
   */
  subscribeToHistoryRange(accountId, startDate, endDate, callback) {
    if (!accountId || !startDate || !endDate) {
      console.error('AccountId, startDate et endDate requis pour subscribeToHistoryRange');
      return () => {};
    }

    const listenerKey = `range_${accountId}_${startDate}_${endDate}`;
    
    // Nettoyer l'ancien listener s'il existe
    if (this.cacheListeners.has(listenerKey)) {
      this.cacheListeners.get(listenerKey)();
      this.cacheListeners.delete(listenerKey);
    }

    const statsRef = collection(db, 'automower_stats', accountId, 'daily');
    const q = query(
      statsRef,
      where('date', '>=', startDate),
      where('date', '<=', endDate),
      orderBy('date', 'desc')
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const stats = snapshot.docs.map(doc => ({
        ...doc.data(),
        firstSeen: doc.data().firstSeen?.toDate(),
        lastSeen: doc.data().lastSeen?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate()
      }));

      callback({
        success: true,
        stats,
        startDate,
        endDate,
        count: stats.length
      });
    }, (error) => {
      console.error('Erreur subscription range:', error);
      callback({
        success: false,
        error: error.message,
        stats: []
      });
    });

    this.cacheListeners.set(listenerKey, unsubscribe);
    return unsubscribe;
  }

  /**
   * Récupérer l'historique d'une date spécifique (sans subscription)
   */
  async getHistoryForDate(accountId, date) {
    if (!accountId || !date) {
      throw new Error('AccountId et date requis');
    }

    try {
      // Format de date attendu : YYYY-MM-DD
      const targetDate = typeof date === 'string' ? date : date.toISOString().split('T')[0];
      
      // Structure correcte: automower_tracking/{accountId}/history/{date}/points
      const historyRef = collection(db, 'automower_tracking', accountId, 'history', targetDate, 'points');
      const q = query(historyRef, orderBy('timestamp', 'asc'), limit(2000));
      const snapshot = await getDocs(q);
      
      const points = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate(),
        recordedAt: doc.data().recordedAt?.toDate()
      }));

      return {
        success: true,
        points,
        date: targetDate,
        count: points.length
      };
    } catch (error) {
      console.error('Erreur getHistoryForDate:', error);
      return {
        success: false,
        error: error.message,
        points: [],
        count: 0
      };
    }
  }

  /**
   * Nettoyer tous les listeners
   */
  cleanup() {
    // Nettoyer les listeners principaux
    this.listeners.forEach((unsubscribe) => {
      unsubscribe();
    });
    this.listeners.clear();

    // Nettoyer les listeners de cache
    this.cacheListeners.forEach((unsubscribe) => {
      unsubscribe();
    });
    this.cacheListeners.clear();
  }

  /**
   * Écoute l'historique de tracking sur plusieurs heures
   */
  subscribeToTrackingHistory(accountId, hours = 24, callback) {
    if (!accountId) {
      console.error('AccountId requis pour subscribeToTrackingHistory');
      return () => {};
    }

    const listenerKey = `tracking_history_${accountId}_${hours}h`;
    
    // Nettoyer l'ancien listener s'il existe
    if (this.cacheListeners.has(listenerKey)) {
      this.cacheListeners.get(listenerKey)();
      this.cacheListeners.delete(listenerKey);
    }

    // Calculer la date de début (maintenant - X heures)
    const now = new Date();
    const startTime = new Date(now.getTime() - (hours * 60 * 60 * 1000));

    // Pour l'instant, on utilise les données d'aujourd'hui
    // TODO: améliorer pour supporter plusieurs jours si nécessaire
    const today = now.toISOString().split('T')[0];
    const historyRef = collection(db, 'automower_tracking', accountId, 'history', today, 'points');
    
    const q = query(
      historyRef, 
      where('timestamp', '>=', startTime),
      orderBy('timestamp', 'desc'), 
      limit(2000)
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const points = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate(),
        recordedAt: doc.data().recordedAt?.toDate()
      }));

      callback(points);
    }, (error) => {
      console.error('Erreur subscription tracking history:', error);
      callback([]);
    });

    this.cacheListeners.set(listenerKey, unsubscribe);
    return unsubscribe;
  }

  /**
   * Écoute les statistiques d'un compte
   */
  subscribeToStats(accountId, callback) {
    if (!accountId) {
      console.error('AccountId requis pour subscribeToStats');
      return () => {};
    }

    const listenerKey = `stats_${accountId}`;
    
    // Nettoyer l'ancien listener s'il existe
    if (this.cacheListeners.has(listenerKey)) {
      this.cacheListeners.get(listenerKey)();
      this.cacheListeners.delete(listenerKey);
    }

    // Pour l'instant, retourner des stats basiques depuis la position actuelle
    // TODO: implémenter de vraies statistiques depuis automower_stats
    return this.subscribeToPosition(accountId, (positionData) => {
      if (positionData.success && positionData.data) {
        const stats = {
          currentStatus: positionData.data.status,
          currentBattery: positionData.data.battery,
          currentZone: positionData.data.workAreaName,
          lastUpdate: positionData.data.updatedAt,
          isStale: positionData.isStale
        };
        callback(stats);
      } else {
        callback(null);
      }
    });
  }

  /**
   * Récupérer les dates disponibles dans l'historique
   */
  async getAvailableDates(accountId) {
    try {
      // Méthode 1: Essayer de lire le document de métadonnées
      try {
        const metadataRef = doc(db, 'automower_tracking', accountId, 'metadata', 'dates');
        
        const metadataDoc = await getDoc(metadataRef);
        
        if (metadataDoc.exists()) {
          const data = metadataDoc.data();
          const dates = data.availableDates || [];
          
          // Toujours inclure aujourd'hui s'il n'y est pas déjà
          const today = new Date().toISOString().split('T')[0];
          const finalDates = dates.includes(today) ? dates : [today, ...dates];
          
          return finalDates;
        } else {
        }
      } catch (metadataError) {
        console.warn('⚠️ Erreur lecture métadonnées:', metadataError);
      }
      
      // Méthode 2 (Fallback): Ancienne méthode de scan des collections
      const allDates = new Set();
      
      // Récupérer depuis automower_tracking/{accountId}/history
      try {
        const historyRef = collection(db, 'automower_tracking', accountId, 'history');
        
        const snapshot = await getDocs(historyRef);
        
        if (snapshot.size > 0) {
          const rawDates = snapshot.docs.map(doc => doc.id);
          
          rawDates.forEach(dateStr => {
            const isValid = /^\d{4}-\d{2}-\d{2}$/.test(dateStr);
            if (isValid) {
              allDates.add(dateStr);
            }
          });
        }
      } catch (historyError) {
        console.warn('⚠️ Erreur scan history:', historyError);
      }
      
      // Récupérer AUSSI depuis automower_stats/{accountId}/daily
      try {
        const statsRef = collection(db, 'automower_stats', accountId, 'daily');
        
        const statsSnapshot = await getDocs(statsRef);
        
        if (statsSnapshot.size > 0) {
          const rawStatsDates = statsSnapshot.docs.map(doc => doc.data().date || doc.id);
          
          rawStatsDates.forEach(dateStr => {
            const isValid = /^\d{4}-\d{2}-\d{2}$/.test(dateStr);
            if (isValid) {
              allDates.add(dateStr);
            }
          });
        }
      } catch (statsError) {
        console.warn('⚠️ Erreur scan stats:', statsError);
      }
      
      // Convertir le Set en array et trier par date décroissante
      const scannedDates = Array.from(allDates).sort((a, b) => b.localeCompare(a));
      
      if (scannedDates.length > 0) {
        // Toujours inclure aujourd'hui
        const today = new Date().toISOString().split('T')[0];
        const finalDates = scannedDates.includes(today) ? scannedDates : [today, ...scannedDates];
        
        return finalDates;
      }
      
      // Méthode 3: Fallback d'urgence - 7 derniers jours
      const fallbackDates = [];
      for (let i = 0; i < 7; i++) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        fallbackDates.push(date.toISOString().split('T')[0]);
      }
      
      return fallbackDates;
      
    } catch (error) {
      console.error('❌ Erreur générale getAvailableDates:', error);
      
      // En cas d'erreur totale, retourner au moins aujourd'hui
      const today = new Date().toISOString().split('T')[0];
      return [today];
    }
  }

  /**
   * Nettoyer un listener spécifique
   */
  cleanupListener(key) {
    if (this.listeners.has(key)) {
      this.listeners.get(key)();
      this.listeners.delete(key);
    }
    
    if (this.cacheListeners.has(key)) {
      this.cacheListeners.get(key)();
      this.cacheListeners.delete(key);
    }
  }
}

export default new AutomowerFirestoreService();